import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import FiberManualRecordOutlinedIcon from '@material-ui/icons/FiberManualRecordOutlined';
import React from 'react';
import { Tooltip } from '@material-ui/core';
import theme from '../mui/theme';
import { instance as axios } from './axios-config';
import { logError } from './errorLogger';

/**
 * Capitalizes first letters of words in string.
 *
 * @param str String to be modified
 * @param lower Whether all other letters should be lowercased
 * @usage capitalize('fix this string');     // -> 'Fix This String'
 *   capitalize('javaSCrIPT');          // -> 'JavaSCrIPT'
 *   capitalize('javaSCrIPT', true);    // -> 'Javascript'
 */
export const Capitalize = (str, lower = false) =>
  (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, (match) => match.toUpperCase());

export function resolvePath(path, obj) {
  return path.split('.').reduce((prev, curr) => (prev ? prev[curr] : null), obj);
}

export const getFilterValues = (arry, property) => {
  const unique = [...new Set(arry.map((item) => resolvePath(property, item)))];
  return unique;
};

export const createClassName = (str) => {
  const _className = str.replace(/\s+/g, '_').toLowerCase();
  return _className;
};

export const getStatusIcon = (value) => {
  switch (value) {
    case 'Not started':
      return (
        <Tooltip title='Not started' placement='top'>
          <FiberManualRecordOutlinedIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.notStarted}
          />
        </Tooltip>
      );
    case 'Not started—Due':
      return (
        <Tooltip title='Not started—Due' placement='top'>
          <FiberManualRecordOutlinedIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.notStartedDue}
          />
        </Tooltip>
      );
    case 'not started':
      return (
        <Tooltip title='not started' placement='top'>
          <FiberManualRecordOutlinedIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.notStarted}
          />
        </Tooltip>
      );
    case 'Incomplete':
    case 'Inactive':
      return (
        <Tooltip title='Incomplete/Inactive' placement='top'>
          <FiberManualRecordIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.incomplete}
          />
        </Tooltip>
      );
    case 'QA Issue':
      return (
        <Tooltip title='QA Issue' placement='top'>
          <FiberManualRecordIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.flagged}
          />
        </Tooltip>
      );
    case 'Pending QA Review':
      return (
        <Tooltip title='Pending QA Review' placement='top'>
          <FiberManualRecordIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.qa_review}
          />
        </Tooltip>
      );
    case 'Pending Review':
      return (
        <Tooltip title='Pending Review' placement='top'>
          <FiberManualRecordIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.unverified}
          />
        </Tooltip>
      );
    case 'Active':
    case 'All QA Issues Resolved':
      return (
        <Tooltip title='Verified/Active' placement='top'>
          <FiberManualRecordIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.complete}
          />
        </Tooltip>
      );
    case 'Verified':
      return (
        <Tooltip title='Verified/Active' placement='top'>
          <FiberManualRecordIcon
            style={{
              width: '12px',
              height: '12px',
              marginRight: '6px',
              marginTop: '2px'
            }}
            htmlColor={theme.palette.status.verified}
          />
        </Tooltip>
      );
    default:
      return null;
  }
};
export const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      resolve(event?.target?.result);
    };
    reader.onerror = (event) => {
      reader.abort();
      reject(event);
    };
    reader.readAsDataURL(file);
  });
export const getUserInitials = (fullName) => {
  let initials = fullName.match(/\b\w/g) || [];
  initials = ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
  return initials;
};

// formData = parsed JSON obj
export const getFormQuestions = (formData) => {

  let questions = {
    allQuestions: [],
    required: []
  };

  if (!formData) return questions;

  if (formData.pages) {
    // Loop through pages and then elements
    formData.pages.forEach((page) => {
      getElementsFromPage(page, questions);
    });
  } else {
    // Loop through elements
    getElementsFromPage(formData, questions);
  }
  return questions;
};

const getElementsFromPage = (page, questions) => {
  page.elements.forEach((element) => {
    processElement(element, questions);
  });
}

const processElement = (element, questions) => {
  if (element.type != "panel") {
    if (element.isRequired) {
      questions.required.push(element.name);
    }
    questions.allQuestions.push(element.name);
  }
  else if (element.elements) {
    element.elements.forEach((subEl) => {
      processElement(subEl, questions);
    });
  }
}

export const getTitleForQuestion = (questionName, formData) => {

  if (questionName === '') {
    return;
  }
  if (!formData) {
    return;
  }

  let title = questionName;
  let form = JSON.parse(formData);

  const checkPage = (page) => {
    page.elements.forEach((element) => {
      checkElement(element);
    })
  }

  const checkElement = (element) => {
    if (element.type === 'panel') {
      if (element.elements) {
        element.elements.forEach((subEl) => {
          checkElement(subEl);
        });
      }
    }
    else {
      if (element.name === questionName) {
        title = element.title;
      }
    }
  }

  if (form.pages) {
    form.pages.forEach((page) => {
      checkPage(page);
    });
  }
  else if (form.elements) {
    checkPage(form);
  }

  console.log(questionName, title);
  return title;
}
